import {
  ENVELOPE_FOLD_SFX,
  ENVELOPE_SEAL_IMAGE,
  ENVELOPE_SEAL_SFX,
  ENVELOPE_SWOOSH_SFX,
} from "@/constants/redemption";
import "./giftFoldEnvelope.css";

/**
 * giftFoldEnvelope(cardEl, opts)
 *
 * Folds a product card into an envelope, seals it, pauses, then slides it off to
 * the right. Motion + markup are self-contained (this file + giftFoldEnvelope.css);
 * the swappable brand seal + sound cues come from constants/redemption. Ported from
 * Recipient-Redemption-Flow-Prototype.html — the design source of truth for this
 * layer's motion + sound.
 *
 * Timing: fold top (0.9s) → fold bottom (0.9s) → seal → pause (1s) → fly (0.85s).
 * These durations mirror the CSS transition durations, so they live with the
 * animation (not in constants). opts.onComplete fires after it has flown away.
 */

// Envelope-half SVGs painted on the flap backs — the class names bind to the
// `.gfe-env-*` fills/strokes in giftFoldEnvelope.css, so this markup stays with it.
const GFE_ENV_TOP =
  '<svg class="gfe-env" viewBox="0 0 200 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">' +
  '<rect x="0" y="0" width="200" height="100" class="gfe-env-body"/>' +
  '<polygon points="1.5,1.5 198.5,1.5 100,99" class="gfe-env-flap"/>' +
  '<path d="M1.5,99 L1.5,1.5 M198.5,99 L198.5,1.5" class="gfe-env-line"/>' +
  "</svg>";
const GFE_ENV_BOT =
  '<svg class="gfe-env" viewBox="0 0 200 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">' +
  '<rect x="0" y="0" width="200" height="100" class="gfe-env-body"/>' +
  '<path d="M1.5,99 L100,1 L198.5,99" class="gfe-env-line"/>' +
  '<path d="M1.5,1 L1.5,99 L198.5,99 L198.5,1" class="gfe-env-line"/>' +
  "</svg>";

export interface GiftFoldOptions {
  /** Fires once the envelope has flown off-screen and been removed. */
  onComplete?: () => void;
  /** Overrides the envelope color (else falls back to --envelope-color / #333). */
  envelopeColor?: string;
  /** Set false to skip the fold/seal/swoosh sound cues. Default true. */
  sound?: boolean;
}

function makeAudio(src: string, volume: number): HTMLAudioElement | null {
  try {
    const a = new Audio(src);
    a.preload = "auto";
    a.volume = volume;
    a.load();
    return a;
  } catch {
    return null;
  }
}

function playAudio(a: HTMLAudioElement | null): void {
  if (!a) return;
  a.currentTime = 0;
  a.play().catch(() => undefined);
}

export function giftFoldEnvelope(cardEl: HTMLElement | null, opts: GiftFoldOptions = {}): void {
  if (!cardEl) {
    opts.onComplete?.();
    return;
  }
  // Respect reduced-motion: the fold-and-fly is large translational + rotational
  // motion (~3.65s), exactly what "reduce" asks us to avoid. Skip it — hide the
  // card and hand off after a short beat so the downstream (opacity-only) fade
  // still reads as intentional. Matches the reduced-motion path every other stage
  // transition already honors.
  const reduced =
    typeof window !== "undefined" &&
    !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  if (reduced) {
    cardEl.style.visibility = "hidden";
    window.setTimeout(() => opts.onComplete?.(), 200);
    return;
  }
  const withSound = opts.sound !== false;
  const rect = cardEl.getBoundingClientRect();
  const w = rect.width;
  // Round to an EVEN height so every fold dimension is a whole pixel. An odd
  // height makes botH/botTop fractional (e.g. 272.5), and a flap positioned on a
  // half-pixel leaves a sub-pixel gap at the seam that renders as a bright
  // hairline where the two folds meet. Even height keeps topH/seam/botH/botTop
  // integer, and the bottom flap folds up to `seam - 2`, overlapping the top
  // flap by 2px of identical envelope color (invisible) so the join is always
  // fully covered — no matter the (product-dependent) card height.
  const h = 2 * Math.round(rect.height / 2);
  const topH = Math.round(h / 4);
  const seam = topH * 2;
  const botTop = (h + seam) / 2 - 1;
  const botH = h - botTop;

  const clone = (topOffset: number): HTMLElement => {
    const c = cardEl.cloneNode(true) as HTMLElement;
    c.classList.add("gfe-clone");
    c.style.top = `${topOffset}px`;
    c.style.width = `${w}px`;
    c.style.height = `${h}px`;
    c.style.visibility = "visible";
    return c;
  };

  const stage = document.createElement("div");
  stage.className = "gfe-stage";
  stage.style.left = `${rect.left}px`;
  stage.style.top = `${rect.top}px`;
  stage.style.width = `${w}px`;
  stage.style.height = `${h}px`;
  if (opts.envelopeColor) stage.style.setProperty("--gfe-env", opts.envelopeColor);

  const asm = document.createElement("div");
  asm.className = "gfe-asm";

  // Middle band (Q2+Q3) — the card content that stays put behind the folds.
  const mid = document.createElement("div");
  mid.className = "gfe-mid";
  mid.style.top = `${topH}px`;
  mid.style.height = `${botTop - topH}px`;
  mid.appendChild(clone(-topH));
  asm.appendChild(mid);

  // Top flap (Q1): front = top slice of the card, back = top half of envelope.
  const top = document.createElement("div");
  top.className = "gfe-flap gfe-top";
  top.style.height = `${topH}px`;
  const tf = document.createElement("div");
  tf.className = "gfe-face gfe-front";
  tf.appendChild(clone(0));
  const tb = document.createElement("div");
  tb.className = "gfe-face gfe-back";
  tb.innerHTML = GFE_ENV_TOP;
  top.appendChild(tf);
  top.appendChild(tb);
  asm.appendChild(top);

  // Bottom flap (Q4): front = bottom slice, back = bottom half of envelope.
  const bot = document.createElement("div");
  bot.className = "gfe-flap gfe-bot";
  bot.style.top = `${botTop}px`;
  bot.style.height = `${botH}px`;
  const bf = document.createElement("div");
  bf.className = "gfe-face gfe-front";
  bf.appendChild(clone(-botTop));
  const bb = document.createElement("div");
  bb.className = "gfe-face gfe-back";
  bb.innerHTML = GFE_ENV_BOT;
  bot.appendChild(bf);
  bot.appendChild(bb);
  asm.appendChild(bot);

  // Branded seal over the join — flies off with the stage.
  const seal = document.createElement("img");
  seal.className = "gfe-seal";
  seal.src = ENVELOPE_SEAL_IMAGE;
  seal.alt = "";
  seal.style.width = `${Math.round(w * 0.22)}px`;
  seal.style.top = `${seam}px`;

  stage.appendChild(asm);
  stage.appendChild(seal);
  document.body.appendChild(stage);
  cardEl.style.visibility = "hidden";

  // Preloaded, user-gesture-triggered (so they may play with sound). Silent-fail.
  const foldSfx = withSound ? makeAudio(ENVELOPE_FOLD_SFX, 0.6) : null;
  const sealSfx = withSound ? makeAudio(ENVELOPE_SEAL_SFX, 0.6) : null;
  const swooshSfx = withSound ? makeAudio(ENVELOPE_SWOOSH_SFX, 0.2) : null;

  const FOLD = 900;
  const PAUSE = 1000;
  const FLY = 850;
  requestAnimationFrame(() => {
    playAudio(foldSfx); // paper fold — spans the down + up folds
    top.classList.add("gfe-folded"); // fold top down
    window.setTimeout(() => bot.classList.add("gfe-folded"), FOLD); // then fold bottom up
    window.setTimeout(() => mid.classList.add("gfe-sealed"), FOLD * 2); // seal seam behind the envelope color
    window.setTimeout(
      () => {
        seal.classList.add("on"); // stamp the branded seal over the join
        playAudio(sealSfx);
      },
      FOLD * 2 + 130,
    );
    window.setTimeout(
      () => {
        stage.classList.add("gfe-flyoff"); // pause, then slide off right
        playAudio(swooshSfx);
      },
      FOLD * 2 + PAUSE,
    );
    window.setTimeout(
      () => {
        stage.remove();
        opts.onComplete?.();
      },
      FOLD * 2 + PAUSE + FLY,
    );
  });
}

export default giftFoldEnvelope;
